import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import kitchenApi from '../services/kitchenApiService';

const KitchenAuthContext = createContext(null);

export function KitchenAuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading]             = useState(true);
  const [user, setUser]                       = useState(null);
  const [userRole, setUserRole]               = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!kitchenApi.checkAuthStatus()) {
        setIsAuthenticated(false);
        return;
      }
      const res = await kitchenApi.getCurrentUser();
      if (res.success) {
        const u = res.data?.user || res.data;
        setUser(u);
        setUserRole(kitchenApi.getUserRole() || u?.role || null);
        setUserPermissions(kitchenApi.getUserPermissions() || u?.permissions || []);
        setIsAuthenticated(true);
      } else {
        kitchenApi.clearToken();
        setIsAuthenticated(false);
      }
    } catch {
      kitchenApi.clearToken();
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    kitchenApi.onUnauthorized(() => {
      setIsAuthenticated(false);
      setUser(null);
      setUserRole(null);
      setUserPermissions([]);
    });
  }, [checkAuth]);

  const login = (userData) => {
    const u = userData?.user || userData;
    setUser(u);
    setUserRole(kitchenApi.getUserRole() || u?.role || null);
    setUserPermissions(kitchenApi.getUserPermissions() || u?.permissions || []);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    await kitchenApi.logout();
    setIsAuthenticated(false);
    setUser(null);
    setUserRole(null);
    setUserPermissions([]);
  };

  // Permission helpers
  const permsSet = new Set(userPermissions || []);
  const isOwnerOrAdmin     = !userRole || ['kitchen', 'super_admin', 'admin'].includes(userRole);
  const isKitchenManager   = userRole === 'kitchen_manager';
  const isFinanceManager   = userRole === 'finance_manager';
  const isSupportExecutive = userRole === 'support_executive';

  const can = {
    orders:     isOwnerOrAdmin || isKitchenManager,
    menu:       isOwnerOrAdmin || isKitchenManager,
    schedule:   isOwnerOrAdmin || isKitchenManager,
    analytics:  isOwnerOrAdmin,
    ratings:    isOwnerOrAdmin || isKitchenManager,
    finance:    isOwnerOrAdmin || isFinanceManager,
    support:    isOwnerOrAdmin || isSupportExecutive,
    team:       isOwnerOrAdmin || permsSet.has('user_management'),
    settings:   isOwnerOrAdmin,
  };

  return (
    <KitchenAuthContext.Provider value={{
      isAuthenticated, isLoading, user, userRole, userPermissions,
      login, logout, checkAuth, can,
    }}>
      {children}
    </KitchenAuthContext.Provider>
  );
}

export function useKitchenAuth() {
  const ctx = useContext(KitchenAuthContext);
  if (!ctx) throw new Error('useKitchenAuth must be used inside KitchenAuthProvider');
  return ctx;
}
